import { useState, type FormEvent } from "react";
import { Button, Field, Skeleton, EmptyState } from "@cntrl/ui";
import { Modal } from "../../releases/Modal.js";
import { formErrorMessage } from "../../../app/hooks/apiError.js";
import { useCapability } from "../../../app/bandContext.js";
import { useEmailStyle, useUpsertEmailStyle, type EmailStyleView } from "../../../app/hooks/useBroadcasts.js";
import styles from "./FanForm.module.scss";

/**
 * The email-style modal — the band's email brand kit (footer, reply-to, brand-kit JSON). Consumed by the
 * v2 email stack; authored + stored at go-live. Full designer is v2 — this is the minimal token state.
 */
export function EmailStyleModal({ onClose }: { onClose: () => void }) {
  const q = useEmailStyle();
  return (
    <Modal title="Email style" size="md" onClose={onClose} dirty="auto">
      {q.isError ? (
        <EmptyState title="Couldn't load your email style" description="Something went wrong." />
      ) : q.isLoading || !q.data ? (
        <div role="status" aria-label="Loading"><Skeleton variant="line" width="70%" /></div>
      ) : (
        <EmailStyleForm style={q.data} onClose={onClose} />
      )}
    </Modal>
  );
}

function EmailStyleForm({ style, onClose }: { style: EmailStyleView; onClose: () => void }) {
  const canManage = useCapability("fans:manage");
  const save = useUpsertEmailStyle();
  const [footerText, setFooterText] = useState(style.footerText ?? "");
  const [replyTo, setReplyTo] = useState(style.replyTo ?? "");
  const [brandKit, setBrandKit] = useState(style.brandKit || "{}");
  const [error, setError] = useState<string | null>(null);

  function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    save.mutate(
      { headerImageMediaId: style.headerImageMediaId ?? null, footerText: footerText || null, replyTo: replyTo || null, brandKit },
      { onSuccess: onClose, onError: (err) => setError(formErrorMessage(err, "Couldn't save the email style")) },
    );
  }

  return (
    <form className={styles.form} onSubmit={submit}>
      <p className={styles.note}>Email delivery ships later — this saves your brand style for when it does.</p>
      <Field label="Reply-to" type="email" value={replyTo} onChange={(e) => setReplyTo(e.currentTarget.value)} disabled={!canManage} />
      <Field label="Footer text" value={footerText} onChange={(e) => setFooterText(e.currentTarget.value)} disabled={!canManage} />
      <div className={styles.row}>
        <span className={styles.label}>Brand kit (JSON)</span>
        <textarea className={styles.textarea} value={brandKit} onChange={(e) => setBrandKit(e.currentTarget.value)} disabled={!canManage} spellCheck={false} />
      </div>
      {error && <p className={styles.formError} role="alert">{error}</p>}
      <div className={styles.actionsEnd}>
        <Button type="button" variant="ghost" onClick={onClose}>Close</Button>
        {canManage && <Button type="submit" variant="primary" loading={save.isPending}>Save</Button>}
      </div>
    </form>
  );
}
